import React, {PropsWithChildren} from 'react';
import {makeStyles, SvgIcon} from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import BuildIcon from '@material-ui/icons/Build';
import PolicyIcon from '@material-ui/icons/Policy';
import CloudIcon from '@material-ui/icons/Cloud';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import FlareOutlinedIcon from '@material-ui/icons/FlareOutlined';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import {configApiRef, identityApiRef, useApi} from "@backstage/core-plugin-api";
import useAsync from 'react-use/esm/useAsync';
import {isSidebarItemVisible} from './sidebarAccess';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  svgIcon: {
    marginRight: "-16px"
  },
  links: {
    cursor: "pointer",
    height: "48px",
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    color: "#b5b5b5",
    overflow: 'hidden',
    fontWeight: "bold",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginLeft: 24
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
    fontWeight: "bold",
    color: "inherit"
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

function getIcon(icon: string) {
  switch (icon) {
    // we shouldn't simply import all icons (increases bundle-size), therefore we expand this switch as we go
    case 'AssessmentOutlinedIcon': return AssessmentOutlinedIcon;
    case 'NotificationsActiveOutlinedIcon': return NotificationsActiveOutlinedIcon;
    case 'DashboardOutlinedIcon': return DashboardOutlinedIcon;
    case 'CreateComponentIcon': return CreateComponentIcon;
    case 'CloudIcon': return CloudIcon;
    case 'PolicyIcon': return PolicyIcon;
    case 'FlareOutlinedIcon': return FlareOutlinedIcon;
    case 'HeadsetMicIcon': return HeadsetMicIcon;
    default: return CreateComponentIcon;
  }
}

export const CustomSidebarItems = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const classes = useSidebarLogoStyles();

  // Auth Backend 0.28 omits the `ent` claim from user tokens, so the ownership
  // refs come from the sign-in identity instead of the token payload.
  const { value: identity } = useAsync(() => identityApi.getBackstageIdentity(), [identityApi]);

  const sidebarConfigs = configApi.getConfigArray('gvf.sidebar');
  const serviceGroupMapping = configApi.getConfigArray('gvf.serviceGroupMapping');

  return (
    <>
      {sidebarConfigs.map(config => {
        const icon = getIcon(config.get('icon'));
        const text = config.getString('text');
        const to = config.getString('to');

        if (!isSidebarItemVisible(to, serviceGroupMapping, identity?.ownershipEntityRefs)) {
          return null; // User is not allowed to see this item
        }

        return (<div key={to} className={classes.links}>
          <SvgIcon component={icon} className={classes.svgIcon}/>
          <Link to={to} target="_self" color="textSecondary" variant="subtitle2" underline="none" className={classes.link}>
            {text}
          </Link>
        </div>)
      })}
    </>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          <CustomSidebarItems />
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
        <SidebarItem icon={BuildIcon} to="devtools" text="Diagnose" />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
